import { PAGINATION_PAGE_PARAM, PAGINATION_PER_PAGE_PARAM } from '@clockout-dev/design-elements';
import {
    ISSUES_COLUMN_PAGE_SIZE,
    ISSUES_PER_PAGE,
    ISSUE_STATUS_CHIPS,
    ISSUE_STATUS_FILTER_ALL,
    ISSUE_STATUS_PARAM,
    ISSUE_VIEWS,
    ISSUE_VIEW_DEFAULT,
    ISSUE_VIEW_PARAM,
    EMPTY_ISSUE_COLUMN,
    SUPPORT_TABS,
    SUPPORT_TAB_ITEMS,
    SUPPORT_TAB_DEFAULT,
    SUPPORT_TAB_PARAM,
} from './issueTracker.constants';
import { ISSUE_STATUS_COLUMNS } from '../common/issueStatusPill/issueStatus.constants';

/**
 * The Jira tab's business logic, composed into `SupportController` so the two
 * tabs share one page shell but not one controller file — the same split
 * `reports.controller.js` uses, where each sub-controller owns one tab and its
 * own URL keys.
 *
 * BOTH VIEWS PAGE SERVER-SIDE, in two different shapes:
 *
 *  - the TABLE asks for one page of everything (or of one status, when a chip
 *    is set) and replaces its rows;
 *  - the BOARD asks once PER COLUMN — `?status=queued&page=2` is literally "the
 *    next chunk of Queued" — and appends as that column is scrolled. Columns
 *    page independently, because Queued being three deep says nothing about Done.
 *
 * Nothing here is Jira-specific beyond the endpoint name: the read model is the
 * provider-agnostic issue-tracker mirror clockout-service serves.
 */
class IssuesController {
    /** Chip value from the URL, falling back to "all" for anything unrecognised. */
    readIssueStatusFromUrl() {
        const raw = this.getSearchParamValue(ISSUE_STATUS_PARAM);
        const isValid = ISSUE_STATUS_CHIPS.some((chip) => chip.key === raw);
        return isValid ? raw : ISSUE_STATUS_FILTER_ALL;
    }

    /** Table or board, from the URL. */
    readIssueViewFromUrl() {
        const raw = this.getSearchParamValue(ISSUE_VIEW_PARAM);
        return Object.values(ISSUE_VIEWS).includes(raw) ? raw : ISSUE_VIEW_DEFAULT;
    }

    // -----------------------------------------------------------------------
    // Table
    // -----------------------------------------------------------------------

    /**
     * One page of the table.
     *
     * A generation token guards against an out-of-order response: changing the
     * chip twice quickly must not let the first, slower reply overwrite the
     * second. Same guard as `reports.controller.js#loadUsers`.
     */
    async loadJiraIssues(page = 1) {
        const generation = (this._issuesGeneration || 0) + 1;
        this._issuesGeneration = generation;

        this.setIssuesLoading(true);

        const { issueStatusFilter, issuesPageSize, issuePageInfo } = this.state;
        const perPage = issuesPageSize || issuePageInfo?.perPage || ISSUES_PER_PAGE;

        const filters = { page, perPage };
        // The "all" sentinel is a UI concept — it is never sent to the API.
        if (issueStatusFilter && issueStatusFilter !== ISSUE_STATUS_FILTER_ALL) {
            filters.status = issueStatusFilter;
        }

        try {
            const data = await this.data.fetchJiraIssues(filters);
            if (this._unmounted || this._issuesGeneration !== generation) return;
            this.setIssuesPage(data?.items, data?.pageinfo, data?.sync);
        } catch (error) {
            if (this._unmounted || this._issuesGeneration !== generation) return;
            console.error('Error loading Jira issues:', error);
            this.setIssuesError();
        } finally {
            if (!this._unmounted && this._issuesGeneration === generation) this.setIssuesLoading(false);
        }
    }

    handleIssuesTableChange(pagination) {
        const nextSize = pagination?.pageSize ?? this.state.issuesPageSize;

        if (nextSize !== this.state.issuesPageSize) {
            // A new page size invalidates the current offset, so start over.
            this.setIssuesPerPage(nextSize, () => this.loadJiraIssues(1));
            return;
        }

        this.loadJiraIssues(pagination?.current || 1);
    }

    handleIssueStatusFilterChange(value) {
        // Back to page 1, or filtering from page 4 lands on an empty page.
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        // "all" means "no filter", so it is removed from the URL rather than written.
        this.setSearchParamValue(ISSUE_STATUS_PARAM, value && value !== ISSUE_STATUS_FILTER_ALL ? value : null);
        this.setIssueStatusFilter(value, () => this.loadJiraIssues(1));
    }

    // -----------------------------------------------------------------------
    // Board
    // -----------------------------------------------------------------------

    /**
     * The statuses the board draws a column for, in the served order.
     *
     * Falls back to the bundled status vocabulary before the map arrives so the
     * board is never briefly column-less — and so it is never briefly a
     * DIFFERENT board. The fallback is `ISSUE_STATUS_COLUMNS`: the same three
     * buckets, in the same order, as `Constants.JIRA.STATUSES.LIST` — the only
     * values the service accepts as `?status=`.
     */
    issueColumnKeys() {
        const served = this.state.issueStatusMap?.statuses;
        if (served?.length) return served.map((entry) => ({ key: entry.key, label: entry.label }));
        return ISSUE_STATUS_COLUMNS;
    }

    /** A column's state, or the empty shape when it has not loaded yet. */
    issueColumn(statusKey) {
        return this.state.issueBoard?.[statusKey] || EMPTY_ISSUE_COLUMN;
    }

    /**
     * Load one column's next page, or its first.
     *
     * `append` is what separates a scroll from a (re)load: appending keeps what
     * is already on screen, which is the whole point of scrolling a column.
     *
     * @param {string} statusKey
     * @param {{ append?: boolean }} [options]
     */
    async loadIssueColumn(statusKey, { append = false } = {}) {
        const column = this.issueColumn(statusKey);

        // One request per column at a time; the sentinel can fire repeatedly
        // while a fetch is still out.
        if (column.loading) return;
        if (append && !column.hasNext) return;

        const page = append ? (column.page || 0) + 1 : 1;
        this.setIssueColumn(statusKey, { loading: true, error: false });

        try {
            const data = await this.data.fetchJiraIssues({
                status: statusKey,
                page,
                perPage: ISSUES_COLUMN_PAGE_SIZE,
            });
            if (this._unmounted) return;

            const incoming = data?.items || [];
            const pageinfo = data?.pageinfo || {};

            this.setIssueColumn(statusKey, {
                items: append ? [...(column.items || []), ...incoming] : incoming,
                page,
                total: typeof pageinfo.count === 'number' ? pageinfo.count : incoming.length,
                hasNext: Boolean(pageinfo.hasNext),
                loading: false,
                error: false,
            });

            // Every column reports the same mirror; the first answer is enough.
            if (data?.sync && !this.state.issueSync) this.setState({ issueSync: data.sync });
        } catch (error) {
            if (this._unmounted) return;
            console.error(`Error loading Jira column "${statusKey}":`, error);
            this.setIssueColumn(statusKey, { loading: false, error: true });
        }
    }

    /** First page of every column, in parallel. */
    async loadIssueBoard() {
        this.resetIssueBoard();
        this.setState({ issueSync: null });
        await Promise.all(this.issueColumnKeys().map(({ key }) => this.loadIssueColumn(key, { append: false })));
    }

    /** The column's scroll sentinel came into view. */
    handleIssueColumnEndReached(statusKey) {
        return this.loadIssueColumn(statusKey, { append: true });
    }

    // -----------------------------------------------------------------------
    // View / tab
    // -----------------------------------------------------------------------

    /**
     * Switching view loads what that view needs, once.
     *
     * The two shapes are not interchangeable — the table holds one page of
     * everything, the board holds pages per status — so neither can be rendered
     * from the other's data.
     */
    handleIssueViewChange(value) {
        if (!Object.values(ISSUE_VIEWS).includes(value) || value === this.state.issueView) return;

        this.setSearchParamValue(ISSUE_VIEW_PARAM, value === ISSUE_VIEW_DEFAULT ? null : value);
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);

        // The board renders no chips, so a filter left set would narrow it with
        // nothing on screen to undo it.
        if (value === ISSUE_VIEWS.KANBAN && this.state.issueStatusFilter !== ISSUE_STATUS_FILTER_ALL) {
            this.setSearchParamValue(ISSUE_STATUS_PARAM, null);
            this.setIssueStatusFilter(ISSUE_STATUS_FILTER_ALL);
        }

        this.setIssueView(value, () => {
            if (value === ISSUE_VIEWS.KANBAN) {
                if (!Object.keys(this.state.issueBoard || {}).length) this.loadIssueBoard();
            } else if (!this.state.issueRows?.length) {
                this.loadJiraIssues(1);
            }
        });
    }

    /**
     * Whether the ACTIVE view has already fetched. Deliberately not "are there
     * rows": an FI with no issues at all would otherwise refetch every time the
     * tab is shown. A loaded board has a key per column even when every column
     * came back empty.
     */
    issuesViewLoaded() {
        return this.state.issueView === ISSUE_VIEWS.KANBAN
            ? Object.keys(this.state.issueBoard || {}).length > 0
            : this.state.issuesLoaded === true;
    }

    /**
     * The failure banner's retry.
     *
     * Reloads whichever view is active from its first page rather than from the
     * page that failed: the failure cleared the rows, so there is no offset left
     * to resume from and page 4 of nothing is not a recovery.
     */
    handleIssuesRetry() {
        return this.loadIssuesForView(1);
    }

    /** Load whichever view the screen is opening on. */
    loadIssuesForView(page = 1) {
        return this.state.issueView === ISSUE_VIEWS.KANBAN ? this.loadIssueBoard() : this.loadJiraIssues(page);
    }

    /**
     * A row or a card. Both open the SAME modal, which fetches the full record
     * — a list row is deliberately too slim to render a detail from.
     */
    handleIssueRowClick(record) {
        if (record?.key) this.openIssueDetail(record.key);
    }

    /**
     * The Support tabs this session actually holds the scope for, in strip
     * order.
     *
     * A tab with no `requiredScope` is always accessible. That branch is load
     * bearing and is why this is not the one-liner
     * `SUPPORT_TAB_ITEMS.filter((t) => this.hasScope(t.requiredScope))` that
     * Dashboard and Treasury use: `ClockoutController.hasScope` returns FALSE
     * for a falsy key, so the Support Tickets tab — which deliberately has none
     * — would vanish for everybody.
     *
     * @returns {{key: string, label: string, requiredScope: string|null}[]}
     */
    getAccessibleSupportTabs() {
        return SUPPORT_TAB_ITEMS.filter((tab) => !tab.requiredScope || this.hasScope(tab.requiredScope));
    }

    /**
     * Whether one tab key is reachable by this session. An unknown key is not.
     * @param {string} key
     * @returns {boolean}
     */
    canAccessSupportTab(key) {
        return this.getAccessibleSupportTabs().some((tab) => tab.key === key);
    }

    /**
     * The tab to open on, from `?tab=`.
     *
     * Lives here rather than with the other `read*FromUrl` helpers on
     * SupportController — those read the tickets table's own params, while this
     * one answers "which tab", which is the question this file already owns
     * both ends of.
     *
     * Only ever lands on a tab this session can see. A deep link, or a stale
     * `?tab=issues` from a role that has since lost `admin:jira:tickets:view`,
     * falls through to the default instead of opening the board — the half a
     * hidden tab strip cannot cover, since the key never passed through it.
     * `initialize()` fetches whatever this returns, so getting it wrong would
     * also fire the Jira requests.
     *
     * The default (Support Tickets) carries no scope of its own, so it is
     * always a legal answer for anyone past the Page-level SUPPORT_VIEW gate.
     *
     * @returns {string}
     */
    readSupportTabFromUrl() {
        const raw = this.getSearchParamValue(SUPPORT_TAB_PARAM);
        const known = Object.values(SUPPORT_TABS).includes(raw);
        return known && this.canAccessSupportTab(raw) ? raw : SUPPORT_TAB_DEFAULT;
    }

    /**
     * Switching tabs. `page` and `perPage` are written by the tickets table and
     * shared by both tabs, so they must be cleared — otherwise page 4 of tickets
     * becomes page 4 of issues, which is usually empty.
     */
    handleSupportTabChange(value) {
        if (value === this.state.supportTab) return;
        // A tab the session cannot see is not a tab it can switch to. The strip
        // never offers it, but `Tabs` also writes `?tab=` and the key can
        // arrive from a pasted URL, so the refusal lives here rather than only
        // in the render.
        if (!this.canAccessSupportTab(value)) return;

        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setSearchParamValue(PAGINATION_PER_PAGE_PARAM, null);

        // Symmetric on purpose. Only the tab the URL opens on fetches, so the
        // OTHER tab has never loaded and must do so the first time it is shown —
        // in both directions. Handling only the issues side is what left the
        // tickets table spinning forever after opening on ?tab=issues.
        this.setSupportTab(value, () => {
            if (value === SUPPORT_TABS.ISSUES) {
                if (!this.issuesViewLoaded()) this.loadIssuesForView(1);
                return;
            }
            if (!this.state.ticketsLoaded) this.loadTicketsPage(1);
        });
    }
}

export default IssuesController;
